import QuantityControl from "@/components/Checkout/components/QuantityControl";
import { ShoppingCartItem } from "@/components/Checkout/types";
import { useAppSelector } from "@/redux/hooks";

import { fetchProductMetadataWithGraphQL } from "@/redux/reducers/checkout/thunk";
import { useApolloClient } from "@apollo/client";
import {
  CampaignType,
  FallbackImage,
  formatMoney,
  getAppConsistentParams,
  getDefaultProductImage,
  getFallbackProductImage,
  getProductUrl,
  getPurchaseRewardAmountForProduct,
  MOBILE_SCREEN_SIZE,
  ProductRewardSearchResult,
  RewardPillWithText,
  TABLET_SCREEN_SIZE,
} from "@brandclub/common-ui";
import { RewardUnlockStrategy } from "@brandclub/types";
import {
  Box,
  Skeleton,
  styled,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCheckoutUI } from "../../../CheckoutUIProvider";
import { updateCheckoutItems } from "@/api/rest/checkoutApi";
import GenericDialog from "@/components/StoreComponents/StoreDialogs/GenericDialog";
import { getHeaderLink } from "@/components/StoreComponents/StoreSinglePageOutlet";

const CartProductContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  textDecoration: "none",
  color: theme.palette.primary.main,

  gap: 20,

  ".product_image": {
    objectFit: "contain",
    width: "72px",
    height: "72px",

    [theme.breakpoints.down("md")]: {
      width: "60px",
      height: "60px",
    },
  },

  ".product_detail": {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    width: "100%",
    gap: 6,
    ".info": {
      display: "flex",
      flexDirection: "column",
      gap: 8,
      [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        gap: 4,
      },
      ".price": {
        display: "flex",
        alignItems: "center",
        fontSize: 16,
        fontWeight: 600,
        gap: 13,
        [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
          gap: 11,
        },
      },
      ".name": {
        fontSize: 12,
        fontWeight: 500,
      },
    },
    ".actions": {
      display: "flex",
      gap: 50,
      alignItems: "center",
      ".quantity": {},
      ".remove": {
        fontSize: 14,
        fontWeight: 500,
        "&:hover": {
          cursor: "pointer",
          textDecoration: "underline",
        },
      },
    },
  },

  "&.large": {
    gap: 30,
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      gap: 25,
    },
    ".product_image": {
      width: 115,
      height: 115,
      [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        width: "92px",
        height: "104px",
      },
    },
    ".product_detail": {
      gap: 10,
      [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        gap: 6,
      },

      ".info": {
        ".price": {
          fontWeight: 600,
          fontSize: 16,
        },
        ".name": {
          fontWeight: 500,
          fontSize: 14,
        },
      },
      ".actions": {
        justifyContent: "space-between",

        ".remove": {
          fontWeight: 500,
          fontSize: 14,
          [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
            fontSize: 12,
          },
        },
      },
    },
  },
}));

// Debounce utility function
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

export const OrderSummaryProduct = ({
  large = false,
  product,
  className,
}: {
  large?: boolean;
  product: ShoppingCartItem;
  className?: string;
}) => {
  const location = useLocation();
  const brandIdFromLocationState = location?.state?.brandId;
  const brandNameFromLocationState = location?.state?.brandName;
  const appConfig = useAppSelector(({ appConfig }) => appConfig);
  const brandEntity = useAppSelector(({ brandEntity }) => brandEntity);
  const navigate = useNavigate();

  const brandName = brandNameFromLocationState ?? brandEntity?.entity?.name;
  const brandId = brandIdFromLocationState ?? brandEntity?.entity?.brandId;

  const emptyBagLinkTo = getHeaderLink(appConfig, brandName, brandId);
  const cartProducts = useAppSelector((state) => state.checkout.products);
  const userProfile = useAppSelector((state) => state.userProfile);
  const cartProduct = cartProducts?.[product.stacklineSku];
  const [productMetadata, setProductMetadata] =
    useState<ProductRewardSearchResult | null>(null);

  const [state, send] = useCheckoutUI();
  const { checkoutIntent } = state.context;

  const checkoutIntentId = checkoutIntent?.checkoutIntentId;

  const apolloClient = useApolloClient();

  useEffect(() => {
    const fetchProductMetadata = async () => {
      const [productMetadata] = await fetchProductMetadataWithGraphQL(
        apolloClient,
        [product.stacklineSku],
        !!userProfile?.userId
      );
      setProductMetadata(productMetadata);
    };
    if (cartProduct) {
      setProductMetadata(cartProduct);
    } else {
      fetchProductMetadata();
    }
  }, [product.stacklineSku, userProfile?.userId, cartProduct, apolloClient]);

  const metadata =
    productMetadata ??
    (product as ShoppingCartItem & ProductRewardSearchResult);

  const retailPrice = product.retailPrice || metadata?.retailPrice;
  const hasActiveInstantReward =
    !!metadata?.hasActiveInstantReward ||
    (!!metadata?.campaignRewards &&
      metadata?.campaignRewards.some(
        (campaignReward) =>
          campaignReward?.campaignType ===
            CampaignType.DailyDealsRewardCampaign ||
          (campaignReward &&
            "rewardUnlockStrategy" in campaignReward &&
            campaignReward.rewardUnlockStrategy ===
              RewardUnlockStrategy.Instant)
      ));
  const rewardAmount = metadata && getPurchaseRewardAmountForProduct(metadata);
  const rewardPercent =
    !!metadata?.retailPrice && !!rewardAmount
      ? (rewardAmount * 100) / metadata.retailPrice
      : 0;

  const isMobileView = useMediaQuery(`(max-width: ${MOBILE_SCREEN_SIZE}px)`);
  const urlSearchParams = getAppConsistentParams(window.location.search);

  // Simple debounced API sync - fire and forget
  const debouncedApiSync = useRef<((updatedItems: any[]) => void) | null>(null);

  // Store current checkoutIntentId in ref to avoid stale closures
  const currentCheckoutIntentId = useRef(checkoutIntentId);
  currentCheckoutIntentId.current = checkoutIntentId;

  // Initialize the debounced function only once
  if (!debouncedApiSync.current) {
    debouncedApiSync.current = debounce(async (updatedItems: any[]) => {
      if (!currentCheckoutIntentId.current) return;

      try {
        // Just sync to server, don't worry about the response
        const res = await updateCheckoutItems({
          checkoutIntentId: currentCheckoutIntentId.current,
          checkoutItems: updatedItems,
        });

        if (res.status === "success" && res.checkoutIntent) {
          send({
            type: "SET_CHECKOUT_INTENT",
            data: {
              checkoutIntent: {
                ...res.checkoutIntent,
                items: updatedItems,
              },
            },
          });
        }
      } catch (error) {
        console.error("Error syncing checkout items to server", error);
        // Could add toast notification here if needed
      }
    }, 300);
  }

  const removeProduct = async () => {
    if (!checkoutIntentId) {
      return;
    }

    // Optimistically update the UI
    const updatedItems =
      checkoutIntent.items?.filter(
        (item) => item.stacklineSku !== product.stacklineSku
      ) || [];

    send({
      type: "SET_CHECKOUT_INTENT",
      data: {
        checkoutIntent: {
          ...checkoutIntent,
          items: updatedItems,
        },
      },
    });

    // Optimistically navigate if this is the last item
    if (updatedItems.length === 0) {
      if (emptyBagLinkTo.external) {
        window.location.href = emptyBagLinkTo.link;
      } else {
        navigate(emptyBagLinkTo.link);
      }
    }

    try {
      const res = await updateCheckoutItems({
        checkoutIntentId,
        checkoutItems: updatedItems,
      });

      if (res.status === "success" && res.checkoutIntent) {
        send({
          type: "SET_CHECKOUT_INTENT",
          data: { checkoutIntent: res.checkoutIntent },
        });
      }
    } catch (error) {
      // Revert optimistic update on error
      send({
        type: "SET_CHECKOUT_INTENT",
        data: { checkoutIntent: checkoutIntent },
      });
      // If this was supposed to be the last item removal, navigate back
      if (updatedItems.length === 0) {
        navigate(-1);
      }
      console.error("Error removing item from checkout", error);
    }
  };

  const onRemove = () => {
    const removingLastItem = checkoutIntent?.items?.length === 1;
    GenericDialog.show({
      title: removingLastItem
        ? "Removing this item will cancel your order — are you sure?"
        : "Are you sure? You might regret it.",
      text: removingLastItem
        ? "Just double-checking before it’s too late."
        : "Just double-checking before we update your order.",
      actionsProps: {
        sx: (theme) => ({
          "&.actions": {
            button: {
              fontSize: 14,
              height: 37,
            },
            [theme.breakpoints.down("md")]: {
              flexDirection: "column-reverse",
              button: {
                height: 44,
                fontSize: 14,
                maxWidth: 355,
              },
            },
          },
        }),
      },
      actions: [
        {
          text: "No, keep it",
          onClick: () => {},
          variant: "outline",
        },
        {
          text: removingLastItem
            ? "Yes, cancel my order"
            : "Yes, remove this item",
          onClick: removeProduct,
          variant: "solid",
        },
      ],
    });
  };

  const handleAddQuantity = async () => {
    if (!checkoutIntentId) {
      return;
    }

    if (product.quantity + 1 > 10) {
      GenericDialog.show({
        title: "Your bag is full",
        text: "You've reached the maximum number of items you can have in your bag for this retailer.",
        textProps: {
          sx: {
            marginBottom: "30px",
            [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
              marginTop: "10px",
            },
            [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
              fontSize: 12,
            },
          },
        },
        actionsProps: {
          sx: {
            "&.actions": {
              [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
                flexDirection: "column-reverse",
                button: {
                  height: 44,
                  fontSize: 14,
                  maxWidth: 355,
                },
              },
            },
          },
        },
        actions: [
          {
            text: "Close",
            onClick: () => {},
            variant: "outline",
          },
        ],
      });
      return;
    }

    // Update local state immediately
    const updatedItems = checkoutIntent.items?.map((item) =>
      item.stacklineSku === product.stacklineSku
        ? { ...item, quantity: item.quantity + 1 }
        : item
    );

    send({
      type: "SET_CHECKOUT_INTENT",
      data: {
        checkoutIntent: {
          ...checkoutIntent,
          items: updatedItems,
        },
      },
    });

    // Quietly sync to server in background
    const checkoutItems =
      updatedItems?.map((item) => ({
        stacklineSku: item.stacklineSku,
        retailerId: item.retailerId,
        retailerSku: item.retailerSku,
        quantity: item.quantity,
      })) || [];

    debouncedApiSync.current?.(checkoutItems);
  };

  const handleMinusQuantity = async () => {
    if (!checkoutIntentId) {
      return;
    } else if (product.quantity - 1 <= 0) {
      onRemove();
      return;
    }

    // Update local state immediately
    const updatedItems = checkoutIntent.items?.map((item) =>
      item.stacklineSku === product.stacklineSku
        ? { ...item, quantity: item.quantity - 1 }
        : item
    );

    send({
      type: "SET_CHECKOUT_INTENT",
      data: {
        checkoutIntent: {
          ...checkoutIntent,
          items: updatedItems,
        },
      },
    });

    // Quietly sync to server in background
    const checkoutItems =
      updatedItems?.map((item) => ({
        stacklineSku: item.stacklineSku,
        retailerId: item.retailerId,
        retailerSku: item.retailerSku,
        quantity: item.quantity,
      })) || [];

    debouncedApiSync.current?.(checkoutItems);
  };

  if (!metadata?.title) {
    return (
      <CartProductContainer>
        <Skeleton className="product_image" />
        <Box className="product_detail">
          <Box className="info">
            <Skeleton className="price" />
            <Skeleton className="name" />
          </Box>
        </Box>
      </CartProductContainer>
    );
  }

  const productUrl = getProductUrl({
    title: metadata.title,
    stacklineSku: metadata.stacklineSku,
    urlSearchParams,
    dtcProductPageUrl: metadata.dtcProductPageUrl,
    storeType: appConfig?.domainConfig?.storeBrandingType,
  });

  const hasLink = productUrl !== "#";

  return (
    <CartProductContainer
      className={[large && "large", className].filter(Boolean).join(" ")}
    >
      {hasLink ? (
        <Link
          to={productUrl}
          style={{ textDecoration: "none" }}
          target="_blank"
        >
          <Box>
            <FallbackImage
              title={metadata?.title ?? product.stacklineSku}
              className="product_image"
              src={getDefaultProductImage(product?.stacklineSku || "")}
              fallbackImage={getFallbackProductImage(
                product?.stacklineSku || ""
              )}
            />
          </Box>
        </Link>
      ) : (
        <Box>
          <FallbackImage
            title={metadata?.title ?? product.stacklineSku}
            className="product_image"
            src={getDefaultProductImage(product?.stacklineSku || "")}
            fallbackImage={getFallbackProductImage(product?.stacklineSku || "")}
          />
        </Box>
      )}
      <Box className={"product_detail"}>
        <Box className={"info"}>
          <Box className={"price"}>
            {formatMoney(retailPrice)}
            {!!rewardAmount && (
              <RewardPillWithText
                rewardAmount={rewardAmount}
                rewardPercentText="reward"
                baseFontSize={isMobileView ? 12 : 14}
                pillContainerStyle={{
                  boxSizing: "border-box",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                rewardPercent={rewardPercent}
                rewardCampaignType={
                  hasActiveInstantReward
                    ? CampaignType.DailyDealsRewardCampaign
                    : CampaignType.PurchaseRewardCampaign
                }
              />
            )}
          </Box>
          <Typography
            className={"name"}
            title={metadata?.title}
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 1,
            }}
          >
            {metadata?.title ?? "Unknown Product"}
          </Typography>
          <QuantityControl
            disabled={!checkoutIntentId}
            large={large}
            count={product.quantity}
            backgroundColor="#ffffff"
            onAddClick={handleAddQuantity}
            onMinusClick={handleMinusQuantity}
            trackedParams={product}
            responsiveBackgroundColor={"#f6f8fa"}
          />
        </Box>
      </Box>
    </CartProductContainer>
  );
};
